// Auth API client
// Uses centralized client for authenticated endpoints with 401 retry logic

import { authenticatedFetch, API_URL } from './client';

// Types
export interface User {
  id: string;
  username: string;
  email: string | null;
  emailVerified: boolean;
  avatarUrl: string | null;
  hasPassword: boolean;
  isAdmin: boolean;
  createdAt: string;
}

export interface AuthResponse {
  user: User;
  message?: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface ApiError {
  error: string;
  message: string;
  details?: Array<{ field: string; message: string }>;
}

// Helper for auth-specific requests (no 401 retry for auth endpoints)
async function authRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Only set Content-Type if there's a body to send
  const headers: HeadersInit = options.body
    ? { 'Content-Type': 'application/json', ...options.headers }
    : { ...options.headers };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw data as ApiError;
  }

  return data as T;
}

// Register a new user
export async function register(data: RegisterData): Promise<AuthResponse> {
  return authRequest<AuthResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Login with email/password
export async function login(data: LoginData): Promise<AuthResponse> {
  return authRequest<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Logout
export async function logout(): Promise<{ message: string }> {
  return authRequest<{ message: string }>('/auth/logout', {
    method: 'POST',
  });
}

// Refresh access token
export async function refreshToken(): Promise<AuthResponse> {
  return authRequest<AuthResponse>('/auth/refresh', {
    method: 'POST',
  });
}

// Get current user (uses authenticatedFetch for 401 retry)
export async function getCurrentUser(): Promise<User> {
  return authenticatedFetch<User>('/users/me');
}

// Verify email
export async function verifyEmail(token: string): Promise<{ message: string }> {
  return authRequest<{ message: string }>('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

// Request password reset
export async function forgotPassword(email: string): Promise<{ message: string }> {
  return authRequest<{ message: string }>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

// Reset password with token
export async function resetPassword(
  token: string,
  password: string
): Promise<AuthResponse> {
  return authRequest<AuthResponse>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

// Update user profile (uses authenticatedFetch for 401 retry)
export async function updateProfile(data: {
  username?: string;
  avatarUrl?: string;
}): Promise<User> {
  return authenticatedFetch<User>('/users/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// Change password (uses authenticatedFetch for 401 retry)
export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>('/users/me/password', {
    method: 'PATCH',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

// Request email change (uses authenticatedFetch for 401 retry)
export async function changeEmail(newEmail: string): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>('/users/me/email', {
    method: 'PATCH',
    body: JSON.stringify({ newEmail }),
  });
}

// OAuth login URLs
export function getGoogleAuthUrl(linkMode = false): string {
  const params = linkMode ? '?linkMode=true' : '';
  return `${API_URL}/auth/google${params}`;
}

export function getDiscordAuthUrl(linkMode = false): string {
  const params = linkMode ? '?linkMode=true' : '';
  return `${API_URL}/auth/discord${params}`;
}

// Linked OAuth accounts
export interface OAuthLink {
  id: string;
  provider: 'google' | 'discord';
  providerUsername: string | null;
  providerEmail: string | null;
  avatarUrl: string | null;
  createdAt: string;
}

// Get linked OAuth accounts (uses authenticatedFetch for 401 retry)
export async function getLinkedAccounts(): Promise<{ links: OAuthLink[] }> {
  return authenticatedFetch<{ links: OAuthLink[] }>('/users/me/oauth');
}

// Unlink OAuth account (uses authenticatedFetch for 401 retry)
export async function unlinkAccount(provider: 'google' | 'discord'): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>(`/users/me/oauth/${provider}`, {
    method: 'DELETE',
  });
}

// Resend verification email (uses authenticatedFetch for 401 retry)
export async function resendVerificationEmail(): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>('/users/me/resend-verification', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
